import { useState } from 'react';
import {
  Typography, Button, FormControl, Container, Checkbox,
  FormControlLabel, InputLabel, Select, MenuItem, FormGroup, Box, Paper,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SecurityIcon from '@mui/icons-material/Security';
import ArchiveIcon from '@mui/icons-material/Archive';
import TuneIcon from '@mui/icons-material/Tune';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MuiFileInput } from 'mui-file-input';
import { sessionActions } from '../store';
import EditAttributesAccordion from './components/EditAttributesAccordion';
import { useTranslation } from '../common/components/LocalizationProvider';
import SelectField from '../common/components/SelectField';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import useCommonDeviceAttributes from '../common/attributes/useCommonDeviceAttributes';
import useCommonUserAttributes from '../common/attributes/useCommonUserAttributes';
import { useCatch } from '../reactHelper';
import useServerAttributes from '../common/attributes/useServerAttributes';
import useMapStyles from '../map/core/useMapStyles';
import { map } from '../map/core/MapView';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { usePremiumTokens } from '../common/theme/usePremiumTokens';
import PremiumTabs, { PremiumTabPanel } from '../common/components/PremiumTabs';
import PremiumInput from '../common/components/premium/PremiumInput';

const ServerPage = () => {
  const { classes } = useSettingsStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  const mapStyles = useMapStyles();
  const commonUserAttributes = useCommonUserAttributes(t);
  const commonDeviceAttributes = useCommonDeviceAttributes(t);
  const serverAttributes = useServerAttributes(t);

  const original = useSelector((state) => state.session.server);
  const [item, setItem] = useState({ ...original });

  const handleFileChange = useCatch(async (newFile) => {
    if (newFile) {
      await fetchOrThrow(`/api/server/file/${newFile.name}`, {
        method: 'POST',
        body: newFile,
      });
    }
  });

  const handleSave = useCatch(async () => {
    const response = await fetchOrThrow('/api/server', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(item),
    });
    dispatch(sessionActions.updateServer(await response.json()));
    navigate(-1);
  });

  // Tab configuration
  const tabs = [
    { label: t('sharedPreferences'), icon: <SettingsIcon /> },
    { label: t('sharedLocation'), icon: <LocationOnIcon /> },
    { label: t('sharedPermissions'), icon: <SecurityIcon /> },
    { label: t('sharedFile'), icon: <ArchiveIcon /> },
    { label: t('sharedAttributes'), icon: <TuneIcon /> },
  ];

  // Simple styles (no nested selectors)
  const checkboxSx = {
    color: tokens?.text?.secondary || '#8b949e',
    '&.Mui-checked': { color: tokens?.accent?.primary || '#00d4aa' },
  };

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'settingsServer']}>
      <Container maxWidth={false} className={classes.container}>
        {item && (
          <Paper
            sx={{
              backgroundColor: 'rgba(22, 27, 34, 0.6)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              borderRadius: tokens?.radius?.lg || '12px',
              border: `1px solid ${tokens?.border?.subtle || 'rgba(255, 255, 255, 0.06)'}`,
              overflow: 'hidden',
            }}
          >
            {/* Premium Tabs */}
            <PremiumTabs
              tabs={tabs}
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
            />

            {/* Tab Content Container */}
            <Box sx={{ px: 3, pb: 3 }}>
              {/* Tab 0: Preferências */}
              <PremiumTabPanel value={activeTab} index={0}>
                <Box display="flex" flexDirection="column" gap={3}>
                  {/* URLs customizadas */}
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <PremiumInput
                      value={item.mapUrl || ''}
                      onChange={(event) => setItem({ ...item, mapUrl: event.target.value })}
                      label={t('mapCustomLabel')}
                    />
                    <PremiumInput
                      value={item.overlayUrl || ''}
                      onChange={(event) => setItem({ ...item, overlayUrl: event.target.value })}
                      label={t('mapOverlayCustom')}
                    />
                  </Box>

                  {/* Mapa e Coordenadas */}
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('mapDefault')}</InputLabel>
                      <Select
                        label={t('mapDefault')}
                        value={item.map || 'locationIqStreets'}
                        onChange={(e) => setItem({ ...item, map: e.target.value })}
                      >
                        {mapStyles.filter((style) => style.available).map((style) => (
                          <MenuItem key={style.id} value={style.id}>
                            <Typography component="span">{style.title}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('settingsCoordinateFormat')}</InputLabel>
                      <Select
                        label={t('settingsCoordinateFormat')}
                        value={item.coordinateFormat || 'dd'}
                        onChange={(event) => setItem({ ...item, coordinateFormat: event.target.value })}
                      >
                        <MenuItem value="dd">{t('sharedDecimalDegrees')}</MenuItem>
                        <MenuItem value="ddm">{t('sharedDegreesDecimalMinutes')}</MenuItem>
                        <MenuItem value="dms">{t('sharedDegreesMinutesSeconds')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Unidades */}
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('settingsSpeedUnit')}</InputLabel>
                      <Select
                        label={t('settingsSpeedUnit')}
                        value={item.attributes.speedUnit || 'kn'}
                        onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, speedUnit: e.target.value } })}
                      >
                        <MenuItem value="kn">{t('sharedKn')}</MenuItem>
                        <MenuItem value="kmh">{t('sharedKmh')}</MenuItem>
                        <MenuItem value="mph">{t('sharedMph')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('settingsDistanceUnit')}</InputLabel>
                      <Select
                        label={t('settingsDistanceUnit')}
                        value={item.attributes.distanceUnit || 'km'}
                        onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, distanceUnit: e.target.value } })}
                      >
                        <MenuItem value="km">{t('sharedKm')}</MenuItem>
                        <MenuItem value="mi">{t('sharedMi')}</MenuItem>
                        <MenuItem value="nmi">{t('sharedNmi')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('settingsAltitudeUnit')}</InputLabel>
                      <Select
                        label={t('settingsAltitudeUnit')}
                        value={item.attributes.altitudeUnit || 'm'}
                        onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, altitudeUnit: e.target.value } })}
                      >
                        <MenuItem value="m">{t('sharedMeters')}</MenuItem>
                        <MenuItem value="ft">{t('sharedFeet')}</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl fullWidth size="small" className="premium-input">
                      <InputLabel>{t('settingsVolumeUnit')}</InputLabel>
                      <Select
                        label={t('settingsVolumeUnit')}
                        value={item.attributes.volumeUnit || 'ltr'}
                        onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, volumeUnit: e.target.value } })}
                      >
                        <MenuItem value="ltr">{t('sharedLiter')}</MenuItem>
                        <MenuItem value="usGal">{t('sharedUsGallon')}</MenuItem>
                        <MenuItem value="impGal">{t('sharedImpGallon')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>

                  {/* Timezone e POI */}
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box flex={1}>
                      <SelectField
                        fullWidth
                        value={item.attributes.timezone}
                        onChange={(e) => setItem({ ...item, attributes: { ...item.attributes, timezone: e.target.value } })}
                        endpoint="/api/server/timezones"
                        keyGetter={(it) => it}
                        titleGetter={(it) => it}
                        label={t('sharedTimezone')}
                      />
                    </Box>
                    <Box flex={1}>
                      <PremiumInput
                        value={item.poiLayer || ''}
                        onChange={(event) => setItem({ ...item, poiLayer: event.target.value })}
                        label={t('mapPoiLayer')}
                      />
                    </Box>
                  </Box>

                  {/* Anúncio */}
                  <PremiumInput
                    value={item.announcement || ''}
                    onChange={(event) => setItem({ ...item, announcement: event.target.value })}
                    label={t('serverAnnouncement')}
                    multiline
                    rows={2}
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={item.forceSettings}
                        onChange={(event) => setItem({ ...item, forceSettings: event.target.checked })}
                        sx={checkboxSx}
                      />
                    }
                    label={t('serverForceSettings')}
                  />
                </Box>
              </PremiumTabPanel>

              {/* Tab 1: Localização */}
              <PremiumTabPanel value={activeTab} index={1}>
                <Box display="flex" flexDirection="column" gap={3}>
                  <Box display="flex" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
                    <PremiumInput
                      type="number"
                      value={item.latitude || 0}
                      onChange={(event) => setItem({ ...item, latitude: Number(event.target.value) })}
                      label={t('positionLatitude')}
                    />
                    <PremiumInput
                      type="number"
                      value={item.longitude || 0}
                      onChange={(event) => setItem({ ...item, longitude: Number(event.target.value) })}
                      label={t('positionLongitude')}
                    />
                    <PremiumInput
                      type="number"
                      value={item.zoom || 0}
                      onChange={(event) => setItem({ ...item, zoom: Number(event.target.value) })}
                      label={t('serverZoom')}
                    />
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const { lng, lat } = map.getCenter();
                      setItem({
                        ...item,
                        latitude: Number(lat.toFixed(6)),
                        longitude: Number(lng.toFixed(6)),
                        zoom: Number(map.getZoom().toFixed(1)),
                      });
                    }}
                    sx={{
                      alignSelf: 'flex-start',
                      borderColor: tokens.accent.primary,
                      color: tokens.accent.primary,
                      borderRadius: tokens.radius.md,
                      textTransform: 'none',
                      '&:hover': {
                        backgroundColor: tokens.accent.subtle,
                        borderColor: tokens.accent.primary,
                      },
                    }}
                  >
                    {t('mapCurrentLocation')}
                  </Button>
                </Box>
              </PremiumTabPanel>

              {/* Tab 2: Permissões */}
              <PremiumTabPanel value={activeTab} index={2}>
                <FormGroup>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.registration}
                          onChange={(event) => setItem({ ...item, registration: event.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('serverRegistration')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.readonly}
                          onChange={(event) => setItem({ ...item, readonly: event.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('serverReadonly')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.deviceReadonly}
                          onChange={(event) => setItem({ ...item, deviceReadonly: event.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('userDeviceReadonly')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.limitCommands}
                          onChange={(event) => setItem({ ...item, limitCommands: event.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('userLimitCommands')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.disableReports}
                          onChange={(event) => setItem({ ...item, disableReports: event.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('userDisableReports')}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={item.fixedEmail}
                          onChange={(e) => setItem({ ...item, fixedEmail: e.target.checked })}
                          sx={checkboxSx}
                        />
                      }
                      label={t('userFixedEmail')}
                    />
                  </Box>
                </FormGroup>
              </PremiumTabPanel>

              {/* Tab 3: Arquivo */}
              <PremiumTabPanel value={activeTab} index={3}>
                <Box display="flex" flexDirection="column" gap={3}>
                  <Typography variant="body2" sx={{ color: tokens?.text?.secondary || '#8b949e' }}>
                    {t('serverFileUploadDescription') || 'Faça upload de arquivos para o servidor'}
                  </Typography>
                  <MuiFileInput
                    placeholder={t('sharedSelectFile')}
                    value={null}
                    onChange={handleFileChange}
                    className="premium-input"
                  />
                </Box>
              </PremiumTabPanel>

              {/* Tab 4: Atributos */}
              <PremiumTabPanel value={activeTab} index={4}>
                <EditAttributesAccordion
                  attributes={item.attributes}
                  setAttributes={(attributes) => setItem({ ...item, attributes })}
                  definitions={{ ...commonUserAttributes, ...commonDeviceAttributes, ...serverAttributes }}
                />
              </PremiumTabPanel>
            </Box>
          </Paper>
        )}

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            sx={{
              borderColor: tokens.border.default,
              color: tokens.text.secondary,
              borderRadius: tokens.radius.md,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                borderColor: tokens.border.strong,
                backgroundColor: tokens.surface.hover,
              },
            }}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{
              backgroundColor: tokens.accent.primary,
              borderRadius: tokens.radius.md,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: tokens.accent.hover,
              },
            }}
          >
            {t('sharedSave')}
          </Button>
        </Box>
      </Container>
    </PageLayout>
  );
};

export default ServerPage;
